import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/shared/Sidebar';
import Header from '../../components/shared/Header';
import {
  apiService,
  ApiError,
  FoodCategoryItem,
  BannerItem,
} from '../../services/api';
import './HomepageManagement.css';

type Tab = 'categories' | 'banners';

/** Photo / upload area */
const ImageIcon: React.FC<{ className?: string; size?: number }> = ({
  className,
  size = 16,
}) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  </svg>
);

const CategoryModal: React.FC<{
  open: boolean;
  mode: 'create' | 'edit';
  initial: FoodCategoryItem | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, mode, initial, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<'FOOD' | 'CUISINE' | ''>('');
  const [isVeg, setIsVeg] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!imageFile) {
      setImageObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImageObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setImageFile(null);
    if (mode === 'edit' && initial) {
      setName(initial.name);
      setType(
        initial.type === 'FOOD' || initial.type === 'CUISINE' ? initial.type : ''
      );
      setIsVeg(initial.isVeg);
      setIsActive(initial.isActive);
      setPriority(initial.priority != null ? String(initial.priority) : '');
    } else {
      setName('');
      setType('');
      setIsVeg(false);
      setIsActive(true);
      setPriority('');
    }
  }, [open, mode, initial]);

  if (!open) return null;

  const imagePreviewSrc =
    imageObjectUrl ||
    (!imageFile && mode === 'edit' && initial?.imageUrl ? initial.imageUrl : null);

  const buildFormData = (): FormData => {
    const fd = new FormData();
    fd.append('name', name.trim());
    if (type) fd.append('type', type);
    if (imageFile) fd.append('image', imageFile);
    fd.append('is_veg', isVeg ? 'true' : 'false');
    fd.append('is_active', isActive ? 'true' : 'false');
    if (priority.trim() !== '') {
      const n = parseInt(priority, 10);
      if (!Number.isNaN(n)) fd.append('priority', String(n));
    }
    return fd;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const fd = buildFormData();
      if (mode === 'create') {
        await apiService.createFoodCategory(fd);
      } else if (initial) {
        await apiService.updateFoodCategory(initial.id, fd);
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="hp-modal-overlay" onClick={onClose}>
      <div className="hp-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="hp-modal-header">
          <h2>{mode === 'create' ? 'Add category' : 'Edit category'}</h2>
          <button type="button" className="hp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="hp-modal-form">
          <div className="hp-modal-hero hp-modal-hero--category">
            <div className="hp-modal-hero-heading">
              <span className="hp-label-with-icon">
                <ImageIcon className="hp-image-icon" />
                Image
              </span>
            </div>
            <div className="hp-modal-hero-frame">
              {imagePreviewSrc ? (
                <img src={imagePreviewSrc} alt="" className="hp-modal-hero-img" />
              ) : (
                <div className="hp-modal-hero-placeholder">
                  <ImageIcon className="hp-modal-hero-placeholder-svg" size={28} />
                  <span className="hp-modal-hero-placeholder-text">No image</span>
                </div>
              )}
            </div>
            <label className="hp-modal-hero-upload">
              <input
                type="file"
                accept="image/*"
                className="hp-sr-only"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
              />
              <ImageIcon className="hp-image-icon hp-image-icon--btn" size={18} />
              <span className="hp-btn hp-btn-secondary hp-btn-sm">
                {imageFile ? 'Change image' : mode === 'edit' && initial?.icon ? 'Replace image' : 'Upload image'}
              </span>
            </label>
          </div>
          <div className="hp-modal-fields">
            {error && <div className="hp-form-error">{error}</div>}
            <label className="hp-label">
              Name <span className="hp-req">*</span>
              <input
                className="hp-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Category name"
              />
            </label>
            <label className="hp-label">
              Type
              <select
                className="hp-input"
                value={type}
                onChange={(e) => setType(e.target.value as 'FOOD' | 'CUISINE' | '')}
              >
                <option value="">—</option>
                <option value="FOOD">FOOD</option>
                <option value="CUISINE">CUISINE</option>
              </select>
            </label>
            <label className="hp-check">
              <input
                type="checkbox"
                checked={isVeg}
                onChange={(e) => setIsVeg(e.target.checked)}
              />
              Vegetarian
            </label>
            <label className="hp-check">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>
            <label className="hp-label">
              Priority
              <input
                className="hp-input"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="Optional integer"
              />
            </label>
            <div className="hp-modal-actions">
              <button type="button" className="hp-btn hp-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="hp-btn hp-btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const BannerModal: React.FC<{
  open: boolean;
  mode: 'create' | 'edit';
  initial: BannerItem | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, mode, initial, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [cta, setCta] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [priority, setPriority] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (!imageFile) {
      setImageObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImageObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setImageFile(null);
    if (mode === 'edit' && initial) {
      setTitle(initial.title);
      setCta(initial.cta);
      setIsActive(initial.isActive);
      setPriority(initial.priority != null ? String(initial.priority) : '');
    } else {
      setTitle('');
      setCta('');
      setIsActive(true);
      setPriority('');
    }
  }, [open, mode, initial]);

  if (!open) return null;

  const bannerImagePreviewSrc =
    imageObjectUrl ||
    (!imageFile && mode === 'edit' && initial?.imageUrl ? initial.imageUrl : null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      if (cta.trim()) fd.append('cta', cta.trim());
      if (imageFile) fd.append('image', imageFile);
      fd.append('is_active', isActive ? 'true' : 'false');
      if (priority.trim() !== '') {
        const n = parseInt(priority, 10);
        if (!Number.isNaN(n)) fd.append('priority', String(n));
      }
      if (mode === 'create') {
        await apiService.createBanner(fd);
      } else if (initial) {
        await apiService.updateBanner(initial.id, fd);
      }
      onSaved();
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="hp-modal-overlay" onClick={onClose}>
      <div className="hp-modal hp-modal--banner" onClick={(ev) => ev.stopPropagation()}>
        <div className="hp-modal-header">
          <h2>{mode === 'create' ? 'Add banner' : 'Edit banner'}</h2>
          <button type="button" className="hp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="hp-modal-form">
          <div className="hp-modal-hero hp-modal-hero--banner">
            <div className="hp-modal-hero-heading">
              <span className="hp-label-with-icon">
                <ImageIcon className="hp-image-icon" />
                Image
              </span>
            </div>
            <div className="hp-modal-hero-frame">
              {bannerImagePreviewSrc ? (
                <img src={bannerImagePreviewSrc} alt="" className="hp-modal-hero-img" />
              ) : (
                <div className="hp-modal-hero-placeholder hp-modal-hero-placeholder--banner">
                  <ImageIcon className="hp-modal-hero-placeholder-svg" size={48} />
                  <span className="hp-modal-hero-placeholder-text">No image</span>
                </div>
              )}
            </div>
            <label className="hp-modal-hero-upload">
              <input
                type="file"
                accept="image/*"
                className="hp-sr-only"
                onChange={(e) => setImageFile(e.target.files?.[0] ?? null)}
              />
              <ImageIcon className="hp-image-icon hp-image-icon--btn" size={18} />
              <span className="hp-btn hp-btn-secondary hp-btn-sm">
                {imageFile ? 'Change image' : mode === 'edit' && initial?.imageUrl ? 'Replace image' : 'Upload image'}
              </span>
            </label>
          </div>
          <div className="hp-modal-fields">
            {error && <div className="hp-form-error">{error}</div>}
            <label className="hp-label">
              Title <span className="hp-req">*</span>
              <input
                className="hp-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="Banner title"
              />
            </label>
            <label className="hp-label">
              CTA (link / text)
              <input
                className="hp-input"
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                placeholder="Optional"
              />
            </label>
            <label className="hp-check">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Active
            </label>
            <label className="hp-label">
              Priority
              <input
                className="hp-input"
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                placeholder="Optional integer"
              />
            </label>
            <div className="hp-modal-actions">
              <button type="button" className="hp-btn hp-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="hp-btn hp-btn-primary" disabled={submitting}>
                {submitting ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const HomepageManagement: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('categories');

  const [categories, setCategories] = useState<FoodCategoryItem[]>([]);
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [loadingCat, setLoadingCat] = useState(true);
  const [loadingBan, setLoadingBan] = useState(false);
  const [errorCat, setErrorCat] = useState<string | null>(null);
  const [errorBan, setErrorBan] = useState<string | null>(null);

  const [catModal, setCatModal] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    item: FoodCategoryItem | null;
  }>({ open: false, mode: 'create', item: null });

  const [banModal, setBanModal] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    item: BannerItem | null;
  }>({ open: false, mode: 'create', item: null });

  const fetchCategories = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoadingCat(true);
      setErrorCat(null);
      const raw = await apiService.getHomeCategories(signal);
      setCategories(raw.map((r) => apiService.mapApiFoodCategoryToItem(r)));
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (err instanceof ApiError) {
        setErrorCat(err.message);
      } else {
        setErrorCat('Failed to load categories.');
      }
      setCategories([]);
    } finally {
      setLoadingCat(false);
    }
  }, []);

  const fetchBanners = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoadingBan(true);
      setErrorBan(null);
      const raw = await apiService.getHomeBanners(signal);
      setBanners(raw.map((r) => apiService.mapApiBannerToItem(r)));
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      if (err instanceof ApiError) {
        setErrorBan(err.message);
      } else {
        setErrorBan('Failed to load banners.');
      }
      setBanners([]);
    } finally {
      setLoadingBan(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    fetchCategories(ac.signal);
    return () => ac.abort();
  }, [fetchCategories]);

  useEffect(() => {
    if (activeTab !== 'banners') return;
    const ac = new AbortController();
    fetchBanners(ac.signal);
    return () => ac.abort();
  }, [activeTab, fetchBanners]);

  const toggleSidebar = () => setSidebarCollapsed((c) => !c);

  return (
    <div className="dashboard-container">
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      <div className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Header page="homepage" />
        <main className="dashboard-content hp-page">
          <div className="hp-header">
            <div>
              <h1 className="hp-title">Homepage management</h1>
              <p className="hp-sub">Food categories and promotional banners</p>
            </div>
          </div>

          <div className="hp-tabs">
            <button
              type="button"
              className={`hp-tab ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => setActiveTab('categories')}
            >
              Categories
            </button>
            <button
              type="button"
              className={`hp-tab ${activeTab === 'banners' ? 'active' : ''}`}
              onClick={() => setActiveTab('banners')}
            >
              Banners
            </button>
          </div>

          {activeTab === 'categories' && (
            <section className="hp-section">
              <div className="hp-section-head">
                <h2>Food categories</h2>
                <button
                  type="button"
                  className="hp-btn hp-btn-primary"
                  onClick={() =>
                    setCatModal({ open: true, mode: 'create', item: null })
                  }
                >
                  + Add category
                </button>
              </div>
              {loadingCat ? (
                <p className="hp-muted">Loading categories…</p>
              ) : errorCat ? (
                <p className="hp-error">{errorCat}</p>
              ) : categories.length === 0 ? (
                <p className="hp-muted">No categories yet. Add one to get started.</p>
              ) : (
                <div className="hp-grid">
                  {categories.map((c) => (
                    <article key={c.id} className="hp-card">
                      <div className="hp-card-image-wrap">
                        {c.imageUrl ? (
                          <img src={c.imageUrl} alt="" className="hp-card-image" />
                        ) : (
                          <div className="hp-card-placeholder">No image</div>
                        )}
                      </div>
                      <div className="hp-card-body">
                        <h3 className="hp-card-title">{c.name}</h3>
                        <div className="hp-card-meta">
                          {c.type && <span className="hp-pill">{c.type}</span>}
                          {c.isVeg && <span className="hp-pill hp-pill-veg">Veg</span>}
                          <span
                            className={`hp-pill ${c.isActive ? 'hp-pill-on' : 'hp-pill-off'}`}
                          >
                            {c.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {c.priority != null && (
                            <span className="hp-pill">Priority {c.priority}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="hp-btn hp-btn-secondary hp-card-edit"
                          onClick={() =>
                            setCatModal({ open: true, mode: 'edit', item: c })
                          }
                        >
                          Edit
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}

          {activeTab === 'banners' && (
            <section className="hp-section">
              <div className="hp-section-head">
                <h2>Banners</h2>
                <button
                  type="button"
                  className="hp-btn hp-btn-primary"
                  onClick={() =>
                    setBanModal({ open: true, mode: 'create', item: null })
                  }
                >
                  + Add banner
                </button>
              </div>
              {loadingBan ? (
                <p className="hp-muted">Loading banners…</p>
              ) : errorBan ? (
                <p className="hp-error">{errorBan}</p>
              ) : banners.length === 0 ? (
                <p className="hp-muted">No banners yet. Add one to get started.</p>
              ) : (
                <div className="hp-grid hp-grid-banners">
                  {banners.map((b) => (
                    <article key={b.id} className="hp-card hp-banner-card">
                      <div className="hp-card-image-wrap hp-banner-image-wrap">
                        {b.imageUrl ? (
                          <img src={b.imageUrl} alt="" className="hp-card-image" />
                        ) : (
                          <div className="hp-card-placeholder">No image</div>
                        )}
                      </div>
                      <div className="hp-card-body">
                        <h3 className="hp-card-title">{b.title}</h3>
                        {b.cta && <p className="hp-banner-cta">{b.cta}</p>}
                        <div className="hp-card-meta">
                          <span
                            className={`hp-pill ${b.isActive ? 'hp-pill-on' : 'hp-pill-off'}`}
                          >
                            {b.isActive ? 'Active' : 'Inactive'}
                          </span>
                          {b.priority != null && (
                            <span className="hp-pill">Priority {b.priority}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="hp-btn hp-btn-secondary hp-card-edit"
                          onClick={() =>
                            setBanModal({ open: true, mode: 'edit', item: b })
                          }
                        >
                          Edit
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}

          <CategoryModal
            open={catModal.open}
            mode={catModal.mode}
            initial={catModal.item}
            onClose={() => setCatModal((s) => ({ ...s, open: false }))}
            onSaved={() => fetchCategories()}
          />
          <BannerModal
            open={banModal.open}
            mode={banModal.mode}
            initial={banModal.item}
            onClose={() => setBanModal((s) => ({ ...s, open: false }))}
            onSaved={() => fetchBanners()}
          />
        </main>
      </div>
    </div>
  );
};

export default HomepageManagement;
